import { useState, useEffect } from 'react';
import { GraduationCap, Plus, Users, Calendar, Clock, Award } from 'lucide-react';
import Layout from '../components/shared/Layout';

interface TrainingSession {
    id: string;
    facilityId: string;
    facilityName: string;
    sessionDate: string;
    instructorName: string;
    instructorCredentials?: string;
    sessionType: 'INITIAL' | 'REFRESHER' | 'SPECIALIZED';
    durationHours: number;
    topicsCovered?: string;
    practicalIncluded: boolean;
}

interface Facility {
    id: string;
    name: string;
}

export default function TrainingPage() {
    const [sessions, setSessions] = useState<TrainingSession[]>([]);
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        facilityId: '',
        sessionDate: '',
        instructorName: '',
        instructorCredentials: '',
        sessionType: 'INITIAL' as const,
        durationHours: 2,
        topicsCovered: [] as string[],
        practicalIncluded: false,
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        const token = localStorage.getItem('token');
        try {
            const [sessionsRes, facilitiesRes] = await Promise.all([
                fetch('https://fire-safety-api.tedguy280.workers.dev/api/training', {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch('https://fire-safety-api.tedguy280.workers.dev/api/facilities', {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            const sessionsData = await sessionsRes.json();
            const facilitiesData = await facilitiesRes.json();

            if (sessionsData.success) setSessions(sessionsData.sessions);
            if (facilitiesData.success) setFacilities(facilitiesData.facilities);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const token = localStorage.getItem('token');

        try {
            const res = await fetch('https://fire-safety-api.tedguy280.workers.dev/api/training', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...formData,
                    sessionDate: new Date(formData.sessionDate).getTime(),
                })
            });

            if (res.ok) {
                setShowForm(false);
                fetchData();
                setFormData({
                    facilityId: '',
                    sessionDate: '',
                    instructorName: '',
                    instructorCredentials: '',
                    sessionType: 'INITIAL',
                    durationHours: 2,
                    topicsCovered: [],
                    practicalIncluded: false,
                });
            }
        } catch (error) {
            console.error('Failed to create session:', error);
        }
    };

    const getSessionTypeLabel = (type: string) => {
        switch (type) {
            case 'INITIAL': return 'Initial Training';
            case 'REFRESHER': return 'Refresher Course';
            case 'SPECIALIZED': return 'Specialized Training';
            default: return type;
        }
    };

    const getSessionTypeColor = (type: string) => {
        switch (type) {
            case 'INITIAL': return 'bg-blue-100 text-blue-800';
            case 'REFRESHER': return 'bg-green-100 text-green-800';
            case 'SPECIALIZED': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <Layout>
            <div className="min-h-screen p-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 animate-fadeIn">
                    <div>
                        <h1 className="text-display-1 font-bold text-charcoal-950 mb-2 flex items-center gap-3">
                            <GraduationCap className="w-10 h-10 text-ember-500" />
                            Training Management
                        </h1>
                        <p className="text-steel-600">
                            Schedule fire safety training sessions and track employee attendance.
                        </p>
                    </div>
                    <button
                        onClick={() => setShowForm(true)}
                        className="btn btn-primary flex items-center gap-2 shadow-lg shadow-ember-500/20 group"
                    >
                        <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                        Schedule Training
                    </button>
                </div>

                {/* Sessions Grid */}
                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="spinner w-12 h-12 border-4 text-ember-500" />
                    </div>
                ) : sessions.length === 0 ? (
                    <div className="card-glass p-12 text-center animate-scaleIn">
                        <GraduationCap className="w-16 h-16 text-steel-400 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-white mb-2">No Training Sessions</h3>
                        <p className="text-steel-400 mb-6">Schedule your first fire safety training session.</p>
                        <button onClick={() => setShowForm(true)} className="btn btn-primary">
                            Schedule Training
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {sessions.map((session, idx) => (
                            <div
                                key={session.id}
                                className="card-glass p-6 animate-slideUp hover:shadow-xl transition-all duration-300"
                                style={{ animationDelay: `${idx * 50}ms` }}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSessionTypeColor(session.sessionType)}`}>
                                        {getSessionTypeLabel(session.sessionType)}
                                    </span>
                                    {session.practicalIncluded && (
                                        <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-medium">
                                            Practical
                                        </span>
                                    )}
                                </div>

                                <h3 className="text-lg font-bold text-white mb-3">{session.facilityName}</h3>

                                <div className="space-y-2 text-sm text-steel-400">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4" />
                                        <span>{new Date(session.sessionDate).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        <span>{session.durationHours} hours</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4" />
                                        <span>{session.instructorName}</span>
                                    </div>
                                    {session.instructorCredentials && (
                                        <div className="flex items-center gap-2">
                                            <Award className="w-4 h-4" />
                                            <span>{session.instructorCredentials}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-4 pt-4 border-t border-steel-700">
                                    <button className="text-sm text-ember-500 hover:text-ember-400 font-medium transition-colors">
                                        View Attendance →
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Modal */}
                {showForm && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="card-glass p-8 w-full max-w-lg animate-scaleIn">
                            <h2 className="text-2xl font-bold text-white mb-6">Schedule Training Session</h2>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-steel-300 mb-2">Facility</label>
                                    <select
                                        className="input w-full"
                                        value={formData.facilityId}
                                        onChange={(e) => setFormData({ ...formData, facilityId: e.target.value })}
                                        required
                                    >
                                        <option value="">Select Facility</option>
                                        {facilities.map(f => (
                                            <option key={f.id} value={f.id}>{f.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-steel-300 mb-2">Session Date</label>
                                        <input
                                            type="date"
                                            className="input w-full"
                                            value={formData.sessionDate}
                                            onChange={(e) => setFormData({ ...formData, sessionDate: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-steel-300 mb-2">Duration (hours)</label>
                                        <input
                                            type="number"
                                            className="input w-full"
                                            value={formData.durationHours}
                                            onChange={(e) => setFormData({ ...formData, durationHours: parseFloat(e.target.value) })}
                                            min="0.5"
                                            step="0.5"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-steel-300 mb-2">Session Type</label>
                                    <select
                                        className="input w-full"
                                        value={formData.sessionType}
                                        onChange={(e) => setFormData({ ...formData, sessionType: e.target.value as any })}
                                    >
                                        <option value="INITIAL">Initial Training</option>
                                        <option value="REFRESHER">Refresher Course</option>
                                        <option value="SPECIALIZED">Specialized Training</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-steel-300 mb-2">Instructor Name</label>
                                    <input
                                        type="text"
                                        className="input w-full"
                                        value={formData.instructorName}
                                        onChange={(e) => setFormData({ ...formData, instructorName: e.target.value })}
                                        placeholder="John Smith"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-steel-300 mb-2">Credentials (optional)</label>
                                    <input
                                        type="text"
                                        className="input w-full"
                                        value={formData.instructorCredentials}
                                        onChange={(e) => setFormData({ ...formData, instructorCredentials: e.target.value })}
                                        placeholder="Certified Fire Safety Instructor"
                                    />
                                </div>

                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="practicalIncluded"
                                        checked={formData.practicalIncluded}
                                        onChange={(e) => setFormData({ ...formData, practicalIncluded: e.target.checked })}
                                        className="w-4 h-4 accent-ember-500"
                                    />
                                    <label htmlFor="practicalIncluded" className="text-sm text-steel-300">
                                        Includes practical exercises
                                    </label>
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="btn btn-secondary flex-1"
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary flex-1">
                                        Schedule Session
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
